import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  IconButton,
  Alert,
  Divider,
  Paper,
  Grid,
} from "@mui/material";
import { IoClose, IoSyncOutline, IoCheckmarkCircleOutline } from "react-icons/io5";
import { MdOutlineStorage, MdErrorOutline } from "react-icons/md";
import { startMongoSync, getMongoSyncStatus } from "../../Service/sync.service";

const MongoSyncModal = ({ open, onClose, onSyncComplete }) => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [syncDone, setSyncDone] = useState(false);
  const pollTimerRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await getMongoSyncStatus();
      if (res && res.status) {
        setSyncing(!!res.inProgress);
        setProgressMsg(res.progress || "");
        if (res.lastSyncResult) {
          setLastResult(res.lastSyncResult);
        }
        if (res.lastError) {
          setErrorMsg(res.lastError);
        }
        return res.inProgress;
      }
    } catch (err) {
      console.error("fetchStatus error:", err);
    }
    return false;
  };

  useEffect(() => {
    if (open) {
      setErrorMsg("");
      setSyncDone(false);
      fetchStatus().then((isInProgress) => {
        if (isInProgress) {
          startPolling();
        }
      });
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [open]);

  const startPolling = () => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const res = await getMongoSyncStatus();
        if (res && res.status) {
          setSyncing(!!res.inProgress);
          setProgressMsg(res.progress || "");
          if (!res.inProgress) {
            stopPolling();
            if (res.lastSyncResult) {
              setLastResult(res.lastSyncResult);
              setSyncDone(true);
            }
            if (res.lastError) {
              setErrorMsg(res.lastError);
            }
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 2000);
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const handleStartSync = async () => {
    setLoading(true);
    setErrorMsg("");
    setSyncDone(false);
    try {
      const res = await startMongoSync();
      setLoading(false);
      if (res && res.status) {
        setSyncing(true);
        setProgressMsg(res.progress || "Sync started...");
        startPolling();
      } else {
        setErrorMsg(res.message || "Failed to trigger sync");
      }
    } catch (err) {
      setLoading(false);
      setErrorMsg(err.message || "Failed to trigger sync");
    }
  };

  const handleClose = () => {
    stopPolling();
    if (syncDone && onSyncComplete) {
      onSyncComplete();
    }
    onClose();
  };

  const handleDoneAndRefresh = () => {
    handleClose();
    window.location.reload();
  };

  return (
    <Dialog
      open={open}
      onClose={syncing ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, pb: 1 }}>
        <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
          <Stack flexDirection="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "8px",
                backgroundColor: "#DA292115",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#DA2921",
                fontSize: "22px",
              }}
            >
              <IoSyncOutline className={syncing ? "spin-icon" : ""} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} fontSize="17px" lineHeight={1.2}>
                MongoDB Atlas Data Sync
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Zero data mismatch synchronization
              </Typography>
            </Box>
          </Stack>
          {!syncing && (
            <IconButton onClick={handleClose} size="small">
              <IoClose fontSize="20px" />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2.5 }}>
        {errorMsg && (
          <Alert severity="error" icon={<MdErrorOutline fontSize="20px" />} sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}

        {syncing ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <CircularProgress
              size={54}
              thickness={4}
              sx={{ color: "#DA2921", mb: 2.5 }}
            />
            <Typography variant="h6" fontWeight={600} fontSize="16px" gutterBottom>
              Synchronizing with MongoDB Atlas...
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                backgroundColor: "#f5f5f5",
                p: 1.5,
                borderRadius: "6px",
                display: "inline-block",
                maxWidth: "100%",
                wordBreak: "break-word",
                fontWeight: 500,
              }}
            >
              {progressMsg || "Checking records in dependency order..."}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
              Please do not close this window while synchronization is in progress.
            </Typography>
          </Box>
        ) : syncDone && lastResult ? (
          <Box sx={{ py: 1 }}>
            <Stack alignItems="center" sx={{ mb: 2 }}>
              <IoCheckmarkCircleOutline style={{ color: "#2e7d32", fontSize: "48px" }} />
              <Typography variant="h6" fontWeight={700} color="#2e7d32" mt={0.5}>
                Sync Completed Successfully!
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Duration: {lastResult.duration || 0} seconds | All records matched
              </Typography>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", backgroundColor: "#fafafa" }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Sync Summary:
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Box sx={{ p: 1.2, backgroundColor: "#fff", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
                    <Typography variant="caption" color="text.secondary">
                      Form Submissions
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      +{lastResult.summary?.submitForm?.inserted || 0} New
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total: {lastResult.summary?.submitForm?.total?.toLocaleString() || "0"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 1.2, backgroundColor: "#fff", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
                    <Typography variant="caption" color="text.secondary">
                      Survey Forms
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      +{lastResult.summary?.surveyForm?.inserted || 0} New
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total: {lastResult.summary?.surveyForm?.total || "0"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 1.2, backgroundColor: "#fff", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
                    <Typography variant="caption" color="text.secondary">
                      Auditors
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      +{lastResult.summary?.auditor?.inserted || 0} New
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total: {lastResult.summary?.auditor?.total || "0"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 1.2, backgroundColor: "#fff", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
                    <Typography variant="caption" color="text.secondary">
                      Groups
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      +{lastResult.summary?.group?.inserted || 0} New
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total: {lastResult.summary?.group?.total || "0"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        ) : (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
              This tool synchronizes survey submissions, survey forms, auditors, and groups from
              the legacy <strong>MongoDB Atlas</strong> database into this system.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: "8px",
                backgroundColor: "#fff8f8",
                borderColor: "#DA292130",
                mb: 2,
              }}
            >
              <Stack flexDirection="row" gap={1.5} alignItems="flex-start">
                <MdOutlineStorage style={{ color: "#DA2921", fontSize: "24px", marginTop: "2px" }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="#DA2921">
                    Safety & Integrity Guarantee:
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    • <strong>Zero Data Mismatch:</strong> Encodes all answers, JSON objects, and dates losslessly.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    • <strong>Safe Incremental Sync:</strong> Inserts only missing records and updates modified documents. Existing records are never deleted.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {lastResult && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Last sync was successful ({lastResult.duration || 0}s).
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        {syncing ? (
          <Button disabled variant="contained" sx={{ textTransform: "none" }}>
            Syncing...
          </Button>
        ) : syncDone ? (
          <Button
            variant="contained"
            onClick={handleDoneAndRefresh}
            sx={{
              backgroundColor: "#2e7d32",
              "&:hover": { backgroundColor: "#1b5e20" },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Done & Refresh Page
          </Button>
        ) : (
          <Stack flexDirection="row" gap={1} width="100%" justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ textTransform: "none", color: "#666", borderColor: "#ccc" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={loading}
              onClick={handleStartSync}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <IoSyncOutline />}
              sx={{
                backgroundColor: "#DA2921",
                "&:hover": { backgroundColor: "#b71c1c" },
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Start Sync Now
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MongoSyncModal;
